import type { EntryPreviewItem } from "./confirmationEntryTypes";

export type ConfirmationEntryIdentity = { id: string; raw: string; identity: string; text: string; sourceStart: number; sourceEnd: number };
export const CONFIRMATION_ENTRY_IDENTITY_ERROR = "多笔新增费用无法唯一对应，已保留原主体与草稿；请恢复原文后逐笔修改，再应用费用";

/** 只用未变化的原文片段确认旧行；重复原文发生增删时仍不能推断删除对象。 */
export function isUnchangedEntrySource(record: Pick<ConfirmationEntryIdentity, "text" | "sourceStart" | "sourceEnd">, item: Pick<EntryPreviewItem, "sourceStart" | "sourceEnd">, text: string): boolean {
  let start = 0, tail = 0;
  while (start < Math.min(record.text.length, text.length) && record.text[start] === text[start]) start++;
  while (tail < Math.min(record.text.length, text.length) && record.text[record.text.length - 1 - tail] === text[text.length - 1 - tail]) tail++;
  return (record.sourceEnd <= start && item.sourceStart === record.sourceStart)
    || (record.sourceStart >= record.text.length - tail && item.sourceStart === record.sourceStart + text.length - record.text.length);
}

/** 先保留未改文本，再匹配唯一业务身份；量价不作身份，歧义不按位置搬移主体。 */
export function reconcileConfirmationEntryIdentities(
  items: readonly EntryPreviewItem[],
  previous: readonly ConfirmationEntryIdentity[],
  identityOf: (item: EntryPreviewItem) => string,
  text: string,
  preserveUnparsed: boolean,
  hasSubjectPatch: (id: string) => boolean,
): { identities: ConfirmationEntryIdentity[]; ambiguous: boolean } {
  const identities = items.map(identityOf);
  const used = new Set<string>();
  const unchangedGroups = new Map<string, boolean>();
  const matches = items.map((item, index) => {
    const same = previous.filter(record => record.identity === identities[index] && record.raw === item.raw);
    const count = items.filter((other, i) => identities[i] === identities[index] && other.raw === item.raw).length;
    const sameIdentityCount = previous.filter(record => record.identity === identities[index]).length === identities.filter(identity => identity === identities[index]).length;
    const group = previous.filter(record => record.identity === identities[index]);
    if (!unchangedGroups.has(identities[index])) unchangedGroups.set(identities[index], sameIdentityCount && group.every(record =>
      group.filter(other => other.raw === record.raw).length === items.filter((other, i) => identities[i] === identities[index] && other.raw === record.raw).length));
    // 同组文本全未改变时允许调序；改价碰到旧原文时，必须证明该原文仍在未修改片段内。
    const found = same.find(record => !used.has(record.id) && (unchangedGroups.get(identities[index])
      || ((same.length === count || sameIdentityCount) && isUnchangedEntrySource(record, item, text))));
    if (found) used.add(found.id);
    return found;
  });
  let nextId = Math.max(0, ...previous.map(record => Number(record.id))) + 1;
  let ambiguous = false;
  const result = items.map((item, index) => {
    let found = matches[index];
    if (!found) {
      const remaining = previous.filter(record => !used.has(record.id) && record.identity === identities[index]);
      const count = items.filter((_, i) => !matches[i] && identities[i] === identities[index]).length;
      if (remaining.length === 1 && count === 1) found = remaining[0];
      else if (remaining.some(record => hasSubjectPatch(record.id))) ambiguous = true;
    }
    if (found) used.add(found.id);
    return { id: found?.id || String(nextId++), raw: item.raw, identity: identities[index], text, sourceStart: item.sourceStart, sourceEnd: item.sourceEnd };
  });
  return { identities: preserveUnparsed ? [...result, ...previous.filter(record => !used.has(record.id))] : result, ambiguous };
}
